import { decimalToPercentage } from '@/components/dueDiligence-page/dueDiligenceRecord/utils';
import { ITemplateStylingSettings } from '@/components/settings-page/platformSettings/templatesSettings/types';
import {
  getPublishedUpdatesTableAssetClassValue,
  getPublishedUpdatesTablePacingValue
} from '@/components/managerUpdates-page/tabs/publishedUpdatesTab/table/table';
import { PublishedInvestmentManagerUpdatesDto, PublishedInvestmentManagerUpdatesSourceEnum } from '@/components/managerUpdates-page/types';
import { TableColumns } from '@/components/table/types';
import { currencyFormatter } from '@/shared/utils/currency';
import { dateFormatter } from '@/shared/utils/date';
import { getEnumKeyByValue } from '@/shared/utils/enums';
import { Text, View } from '@react-pdf/renderer';
import { AIFieldType, DocumentRecordSentimentFlag } from '@trace-8/trace8-schema';
import { format } from 'date-fns';
import { TFunction } from 'i18next';
import { DefaultTheme } from 'styled-components/dist/types';

const getTableCellValue = (type: string, value: string, commentary: string): string => {
  if (!value && !commentary) {
    return '--';
  }

  switch (type) {
    case getEnumKeyByValue(AIFieldType, AIFieldType.LONG_FORM_CONTENT):
      return value || '--';
    case getEnumKeyByValue(AIFieldType, AIFieldType.SHORT_TEXT):
      return value || '--';
    case getEnumKeyByValue(AIFieldType, AIFieldType.NUMBER):
      if (value && commentary) {
        return `${value} | ${commentary}`;
      }
      return value || commentary;
    case getEnumKeyByValue(AIFieldType, AIFieldType.DOLLAR):
      if (value && commentary) {
        return `${currencyFormatter.format(+value)} | ${commentary}`;
      }
      return value ? currencyFormatter.format(+value) : commentary;
    case getEnumKeyByValue(AIFieldType, AIFieldType.PERCENTAGE):
      if (value && commentary) {
        return `${value ? decimalToPercentage(value) : ''} | ${commentary}`;
      }
      return value ? decimalToPercentage(value) : commentary;
    case getEnumKeyByValue(AIFieldType, AIFieldType.DATE):
      if (value && commentary) {
        return `${value ? dateFormatter(format(new Date(value), 'yyyy-MM-dd'), 'yyyy-MM-dd') : ''} | ${commentary}`;
      }
      return value ? dateFormatter(format(new Date(value), 'yyyy-MM-dd'), 'yyyy-MM-dd') : commentary;
    default:
      return '--';
  }
};

const getSentimentFlagColors = (sentimentFlag: DocumentRecordSentimentFlag) => {
  switch (sentimentFlag) {
    case DocumentRecordSentimentFlag.Positive:
      return {
        color: '#6aa94f',
        backgroundColor: '#f0fcec'
      };
    case DocumentRecordSentimentFlag.Negative:
      return {
        color: '#cc0100',
        backgroundColor: '#ffebec'
      };
  }
  return {
    color: '#474747',
    backgroundColor: '#f3f3f3'
  };
};

interface UpdatesSummaryPDFTemplateTableProps {
  publishedUpdatesTableColumns: TableColumns[];
  publishedUpdates: PublishedInvestmentManagerUpdatesDto[];
  templateStylingData: ITemplateStylingSettings;
  source: PublishedInvestmentManagerUpdatesSourceEnum;
  theme: DefaultTheme;
  t: TFunction<'enums', undefined, 'enums'>;
}

export const UpdatesSummaryPDFTemplateTable = ({
  publishedUpdatesTableColumns,
  publishedUpdates,
  templateStylingData,
  source,
  theme,
  t
}: UpdatesSummaryPDFTemplateTableProps) => {
  const tableColumnsLength = publishedUpdatesTableColumns.length;
  const firstTwoColumnsWidth = 15;
  const remainingColumnsCount = Math.max(1, tableColumnsLength - 2);
  const remainingColumnsWidth = (100 - firstTwoColumnsWidth * 2) / remainingColumnsCount;

  const equalColumnWidth = remainingColumnsWidth > firstTwoColumnsWidth ? 100 / tableColumnsLength : null;

  const getColumnWidth = (index: number): string => {
    if (equalColumnWidth !== null) {
      return `${equalColumnWidth.toFixed(2)}%`;
    }
    if (index < 2) {
      return `${firstTwoColumnsWidth.toFixed(2)}%`;
    }
    return `${remainingColumnsWidth.toFixed(2)}%`;
  };

  const shouldWrapRow = (row: PublishedInvestmentManagerUpdatesDto): boolean => {
    const { legalEntityCommittedCapital } = row || {};

    if (legalEntityCommittedCapital?.length > 5) return true;

    const hasVeryLongText = row.publishedUpdates?.some((update) => {
      const cellValue = getTableCellValue(update.documentField.type, update?.documentFieldAnswer?.value, update?.documentFieldAnswer?.commentary);
      return cellValue && cellValue.length > 1000;
    });

    return hasVeryLongText;
  };

  return (
    <View style={{ fontSize: '8px', padding: '0 40px', fontFamily: 'Open Sans', color: theme.font.base }}>
      <View style={{ display: 'flex', flexDirection: 'row', alignItems: 'stretch' }} fixed>
        {publishedUpdatesTableColumns.map((column, columnIndex) => {
          const isLastColumn = columnIndex === tableColumnsLength - 1;
          return (
            <View
              key={column.key || column?.keySort}
              style={{
                display: 'flex',
                flexDirection: 'row',
                alignItems: 'center',
                justifyContent: 'center',
                border: `1px solid ${theme.border.base}`,
                borderRight: isLastColumn ? `1px solid ${theme.border.base}` : 0,
                width: getColumnWidth(columnIndex),
                padding: '5px 4px',
                color: theme.context.light,
                backgroundColor: templateStylingData?.accentColor || '#5A6771'
              }}
            >
              <Text hyphenationCallback={(word) => [word]}>{column.title}</Text>
            </View>
          );
        })}
      </View>
      <View>
        {publishedUpdates.map((row, index) => {
          const { investment, legalEntityCommittedCapital } = row || {};
          const assetClassValue = getPublishedUpdatesTableAssetClassValue(investment);
          const pacingValue = getPublishedUpdatesTablePacingValue(investment, t);
          const shouldWrap = shouldWrapRow(row);

          return (
            <View key={investment?.id ?? index} style={{ display: 'flex', flexDirection: 'row', alignItems: 'stretch' }} wrap={shouldWrap}>
              <View
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '5px',
                  border: `1px solid ${theme.border.base}`,
                  borderTop: 0,
                  borderRight: 0,
                  width: getColumnWidth(0),
                  padding: '5px 8px'
                }}
              >
                <Text style={{ color: theme.font.weak }}>{`${assetClassValue} (${pacingValue})`}</Text>
                <Text style={{ fontWeight: 600 }}>{investment?.name}</Text>
                {source === PublishedInvestmentManagerUpdatesSourceEnum.UPDATES_TAB && (
                  <View style={{ display: 'flex', flexDirection: 'column', gap: '5px' }}>
                    {legalEntityCommittedCapital?.map((legalEntityCommittedCapitalItem) => {
                      const { entityName, committedCapital } = legalEntityCommittedCapitalItem || {};

                      return (
                        <View key={entityName + committedCapital}>
                          <Text>{entityName}: </Text>
                          <Text style={{ fontWeight: 600 }}>{currencyFormatter.format(+committedCapital)}</Text>
                        </View>
                      );
                    })}
                  </View>
                )}
              </View>
              {row.publishedUpdates.map((update, updateIndex) => (
                <View
                  key={`${row?.investment?.id ?? index}-${update?.documentField?.id}`}
                  style={{
                    display: 'flex',
                    flexDirection: 'row',
                    border: `1px solid ${theme.border.base}`,
                    borderTop: 0,
                    borderRight: 0,
                    width: getColumnWidth(updateIndex + 1),
                    padding: '5px 8px'
                  }}
                >
                  <Text>
                    {getTableCellValue(update.documentField.type, update?.documentFieldAnswer?.value, update?.documentFieldAnswer?.commentary)}
                  </Text>
                </View>
              ))}
              <View
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '8px',
                  border: `1px solid ${theme.border.base}`,
                  borderTop: 0,
                  width: getColumnWidth(tableColumnsLength - 1),
                  padding: '5px 8px'
                }}
              >
                <Text>{row?.lastUpdate ? dateFormatter(format(new Date(row?.lastUpdate), 'yyyy-MM-dd'), 'yyyy-MM-dd') : '--'}</Text>
                {row?.sentimentFlag && row?.sentimentFlagLabel && (
                  <Text
                    style={{
                      color: getSentimentFlagColors(row?.sentimentFlag).color,
                      backgroundColor: getSentimentFlagColors(row?.sentimentFlag).backgroundColor,
                      padding: '2px 5px',
                      borderRadius: '3px',
                      textAlign: 'center'
                    }}
                  >
                    {row?.sentimentFlagLabel}
                  </Text>
                )}
              </View>
            </View>
          );
        })}
      </View>
    </View>
  );
};
